import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// Single source of auth for the whole admin app. Every API call goes through the
// global axios instance; this module is the ONLY place that attaches the bearer,
// and it attaches it ONLY to the configured API origin (never leaks the token to
// any other host). Replaces: the dead apiClient interceptor, forecastApi's global
// mutation + dead `authToken` key, and authProvider.check's `headers.common` clobber.

const API_BASE_URL = import.meta.env.VITE_API_URL
// Fail closed at init: without a valid API URL we cannot scope the bearer safely.
if (!API_BASE_URL) {
  throw new Error('VITE_API_URL is required for admin API auth.')
}
// Origin used to decide whether a request is API-bound. Normalized so path /
// trailing-slash / explicit-port variants in VITE_API_URL don't break `===`.
const API_ORIGIN = new URL(API_BASE_URL).origin

type TokenGetter = () => Promise<string>

let tokenGetter: TokenGetter | null = null
let authFailureHandler: (() => void) | null = null
// `authSettled` is the concrete "post-auth" signal the response handler consults:
// only redirect on failures AFTER initial auth has settled, so pre-auth / callback
// -stage 401s don't loop the login redirect.
let authSettled = false
let redirecting = false

export function setTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter
}
export function clearTokenGetter(): void {
  tokenGetter = null
}
export function setAuthFailureHandler(handler: () => void): void {
  authFailureHandler = handler
}
export function setAuthSettled(settled: boolean): void {
  authSettled = settled
}

// axios's own isAbsoluteURL test (matches http(s):// and protocol-relative //host).
function isAbsoluteURL(url: string): boolean {
  return /^([a-z][a-z\d+\-.]*?:)?\/\//i.test(url)
}

// The origin axios will ACTUALLY dispatch to, replicating buildFullPath — including
// `allowAbsoluteUrls: false`, where axios ignores an absolute `url` and combines it
// onto `baseURL`. Computing origin any other way (e.g. a bare `new URL(url, base)`)
// could see the API origin while axios sends elsewhere, leaking the bearer.
function resolveOrigin(config: InternalAxiosRequestConfig): string | null {
  const url = config.url ?? ''
  const base = config.baseURL ?? axios.defaults.baseURL ?? ''
  const allowAbsoluteUrls = (config as { allowAbsoluteUrls?: boolean })
    .allowAbsoluteUrls
  let fullPath: string
  if (base && (!isAbsoluteURL(url) || allowAbsoluteUrls === false)) {
    // combineURLs: strip base trailing slashes + url leading slashes, then join.
    fullPath = base.replace(/\/+$/, '') + '/' + url.replace(/^\/+/, '')
  } else {
    fullPath = url
  }
  try {
    return new URL(fullPath, window.location.origin).origin
  } catch {
    return null
  }
}

// "Explicit Authorization" = a present, non-blank credential. Missing / null /
// empty / whitespace-only counts as ABSENT so a blank header can't bypass fail-closed.
function hasExplicitAuth(config: InternalAxiosRequestConfig): boolean {
  const h = config.headers
  let value: unknown
  if (h && typeof (h as { get?: unknown }).get === 'function') {
    value = (h as { get: (name: string) => unknown }).get('Authorization')
  } else if (h) {
    const obj = h as Record<string, unknown>
    value = obj.Authorization ?? obj.authorization
  }
  return typeof value === 'string' && value.trim().length > 0
}

// One guarded logout+redirect for real auth failures. No-op until auth has settled
// (so pre-auth/callback 401s are exempt) and once per session (redirect-storm guard).
function handleAuthFailure(): void {
  if (!authSettled || redirecting) return
  redirecting = true
  if (authFailureHandler) {
    authFailureHandler()
  } else if (window.location.pathname !== '/login') {
    window.location.assign('/login')
  }
}

let installed = false

export function installAuthAxios(): void {
  if (installed) return // idempotent: safe under StrictMode double-invoke and HMR
  installed = true

  axios.defaults.baseURL = API_BASE_URL
  axios.defaults.headers.common['Content-Type'] = 'application/json'

  axios.interceptors.request.use(async (config) => {
    // Only ever attach the bearer to the configured API origin.
    if (resolveOrigin(config) !== API_ORIGIN) return config
    // Caller-supplied bearer wins and is exempt from fail-closed rejection.
    if (hasExplicitAuth(config)) return config

    if (!tokenGetter) {
      handleAuthFailure()
      throw new AxiosError('Auth not initialized', 'ERR_AUTH_NOT_READY', config)
    }
    let token: string
    try {
      token = await tokenGetter()
    } catch (err) {
      // getAccessTokenSilently can reject mid-session (login_required after
      // expiry / refresh-rotation revocation).
      handleAuthFailure()
      throw err
    }
    if (!token) {
      handleAuthFailure()
      throw new AxiosError('No auth token available', 'ERR_NO_TOKEN', config)
    }
    config.headers.Authorization = `Bearer ${token}`
    return config
  })

  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const config = error.config
      const isApiOrigin = config ? resolveOrigin(config) === API_ORIGIN : false
      if (isApiOrigin && error.response?.status === 401) {
        handleAuthFailure()
      }
      return Promise.reject(error)
    }
  )
}
